import { formatEur } from './charts/IbcsTooltip';
import VarianceBar from './charts/VarianceBar';
import type { OverviewData, ForecastData } from '../../types/cashCockpit';

interface Props {
    overview: OverviewData | null;
    forecast: ForecastData | null;
}

/**
 * IBCS 3-KPI hero band: Total Cash, Net Change (13W), Liquidity Runway.
 */
export default function HeroKpis({ overview, forecast }: Props) {
    const totalCash = overview?.kpis.totalCashEUR ?? 0;
    const deltaMoM = overview?.kpis.deltaMoM ?? null;
    const deltaMoMPct = overview?.kpis.deltaMoMPct ?? null;

    // Net 13-week change
    const fc = forecast?.forecast ?? [];
    const netChange13W = fc.length
        ? fc[fc.length - 1].closingCashEUR - fc[0].openingCashEUR
        : null;

    // Liquidity runway = opening cash / avg weekly outflow
    const avgWeeklyOutflow = fc.length
        ? fc.reduce((s, w) => s + Math.abs(w.apOutflowEUR + w.plannedOutflowEUR), 0) / fc.length
        : 0;
    const runwayWeeks = avgWeeklyOutflow > 0 && fc.length
        ? Math.round((fc[0].openingCashEUR / avgWeeklyOutflow) * 10) / 10
        : null;

    return (
        <div className="grid grid-cols-3 gap-4">
            <Tile
                label="Total Cash (Group)"
                primaryValue={formatEur(totalCash)}
                sub={
                    <span className="flex items-center gap-1">
                        <span className="text-gray-500 text-xs">vs prev. month:</span>
                        <VarianceBar amount={deltaMoM} pct={deltaMoMPct} compact />
                    </span>
                }
            />
            <Tile
                label="Net Change (13W Forecast)"
                primaryValue={netChange13W != null ? formatEur(netChange13W) : '—'}
                sub={<span className="text-gray-500 text-xs">rolling 13-week projection</span>}
                emphasis={netChange13W == null ? 'neutral' : netChange13W >= 0 ? 'positive' : 'negative'}
            />
            <Tile
                label="Liquidity Runway"
                primaryValue={runwayWeeks != null ? `${runwayWeeks} weeks` : '—'}
                sub={<span className="text-gray-500 text-xs">at current avg burn</span>}
                emphasis={runwayWeeks == null ? 'neutral' : runwayWeeks >= 13 ? 'positive' : runwayWeeks >= 6 ? 'neutral' : 'negative'}
            />
        </div>
    );
}

function Tile({
    label, primaryValue, sub, emphasis = 'neutral',
}: { label: string; primaryValue: string; sub?: React.ReactNode; emphasis?: 'positive' | 'negative' | 'neutral' }) {
    const valueColor = emphasis === 'positive' ? 'text-[#2C7BB6]'
        : emphasis === 'negative' ? 'text-[#D7191C]'
        : 'text-gray-900';
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-1">
            <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
            <div className={`text-3xl font-semibold font-mono tabular-nums ${valueColor}`}>{primaryValue}</div>
            {sub && <div className="mt-1">{sub}</div>}
        </div>
    );
}
